import { useState } from "react";
import type { Message, Channel } from "../hooks/useChat";
import { useAuth } from "../hooks/useAuth";
import { useCrypto, RecipientNotReadyError, type EncryptFanoutMeta } from "../hooks/useCrypto";
import { Avatar } from "./Avatar";
import { AuthImage } from "./AuthImage";
import { api, ApiError } from "../lib/api";
import { ws } from "../lib/ws";
import { safeMessageText, humanizeFileTokens } from "../lib/safeMessageText";
import styles from "./ChatView.module.css";

/** Extract file info from [file:id:mime:name] token */
function parseFileToken(text: string): { fileId: string; contentType: string; filename: string } | null {
  const match = text.match(/\[file:([a-f0-9-]+):([^:]+):([^\]]+)\]/);
  if (!match) return null;
  return { fileId: match[1]!, contentType: match[2]!, filename: match[3]! };
}

/** Get display name for a channel (DM: partner name, Group: channel name) */
function channelDisplayName(channel: Channel, currentUserId?: string): string {
  if (channel.type === "dm" && channel.members && currentUserId) {
    const partner = channel.members.find((m) => m.userId !== currentUserId);
    if (partner?.displayName) return partner.displayName;
  }
  return channel.name ?? "Chat";
}

export interface ForwardDialogProps {
  forwardMessage: Message;
  channels: Channel[];
  currentChannelId: string;
  onClose: () => void;
}

export function ForwardDialog({ forwardMessage, channels, currentChannelId, onClose }: ForwardDialogProps) {
  const currentUserId = useAuth.getState().user?.id;
  const { encryptForAllDevices, encryptForGroup, initialized: e2eeReady } = useCrypto();
  const [sending, setSending] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

  // Get raw message text (with file tokens preserved)
  const rawText = safeMessageText(forwardMessage);
  const fileInfo = parseFileToken(rawText);
  const isImage = fileInfo && fileInfo.contentType.startsWith("image/");

  const handleForward = async () => {
    if (sending || !selectedChannel) return;
    setSending(true);

    try {
      // Keep raw file tokens so images/files render in target chat
      const plaintext = `↗️ Weitergeleitet:\n${rawText}`;
      const otherMembers = selectedChannel.members?.filter((m) => m.userId !== currentUserId) ?? [];

      if (e2eeReady && otherMembers.length > 0) {
        if (selectedChannel.type === "dm") {
          const recipientIds = otherMembers.map((m) => m.userId);

          const encryptOnce = async () => {
            const meta: EncryptFanoutMeta = { peerDevicesSeen: 0, peerEnvelopesProduced: 0, skipped: [] };
            const recipients = await encryptForAllDevices(recipientIds, plaintext, meta);
            const hasPeerRecipient = recipients.some((r) => {
              try {
                const env = JSON.parse(r.ciphertext);
                return !env.selfEncrypted;
              } catch { return true; }
            });
            // partial=true when any server-known device could not be encrypted to
            // this attempt (peer device fail → meta.skipped, or own-device retry
            // fail → meta.ownDeviceFailed). Caller must set partial:true on the POST
            // so the server triggers enqueueBackfillForUncovered (peer devices) and
            // the own-device gap is healed via the server's backup mechanism.
            const partial = meta.skipped.length > 0 || !!meta.ownDeviceFailed;
            return { recipients, hasPeerRecipient, peerDevicesSeen: meta.peerDevicesSeen, partial };
          };

          const first = await encryptOnce();
          if (first.recipients.length > 0 && first.hasPeerRecipient) {
            try {
              await api.post(`/channels/${selectedChannel.id}/messages`, {
                recipients: first.recipients,
                contentType: "e2ee",
                ...(first.partial ? { partial: true } : {}),
              });
            } catch (err) {
              // Stale device snapshot: re-encrypt with fresh /users/keys and retry once.
              if (err instanceof ApiError && err.status === 400 && err.code === "unknown_recipient_device") {
                console.warn("[Forward] Recipient device unknown, refreshing keys and retrying once");
                const retry = await encryptOnce();
                if (retry.recipients.length === 0 || !retry.hasPeerRecipient) {
                  throw new RecipientNotReadyError(
                    otherMembers.filter((m) => !m.isBot).map((m) => m.userId),
                  );
                }
                await api.post(`/channels/${selectedChannel.id}/messages`, {
                  recipients: retry.recipients,
                  contentType: "e2ee",
                  ...(retry.partial ? { partial: true } : {}),
                });
              } else {
                throw err;
              }
            }
            onClose();
            return;
          }
          // No peer envelope this attempt.
          const allOtherMembersAreBots = otherMembers.every((m) => m.isBot);
          if (!allOtherMembersAreBots) {
            // Fix 1: only fail loud when the recipient has ZERO registered devices.
            // If they have devices but are unreachable now, optimistically send our
            // (self) envelopes — never plaintext — and let async backfill / recovery
            // deliver the forwarded message once the peer device is reachable.
            if (first.peerDevicesSeen > 0 && first.recipients.length > 0) {
              await api.post(`/channels/${selectedChannel.id}/messages`, {
                recipients: first.recipients,
                contentType: "e2ee",
                // All peer devices failed → peerDevicesSeen>0 but no peer envelopes.
                // Always partial here: meta.skipped covers the failed peer slots.
                partial: true,
              });
              onClose();
              return;
            }
            throw new RecipientNotReadyError(
              otherMembers.filter((m) => !m.isBot).map((m) => m.userId),
            );
          }
        } else if (selectedChannel.type === "group") {
          const memberIds = otherMembers.map((m) => m.userId);
          const ciphertext = await encryptForGroup(selectedChannel.id, memberIds, plaintext);
          ws.send({ type: "send_message", channelId: selectedChannel.id, ciphertext, contentType: "e2ee" });
          onClose();
          return;
        }
      }

      // Fallback: plaintext (only reachable when E2EE is unavailable or every
      // peer is a bot without E2EE — see guard above).
      ws.send({ type: "send_message", channelId: selectedChannel.id, ciphertext: plaintext, contentType: "text" });
      onClose();
    } catch (err) {
      console.error("[Forward] Failed to forward message:", err);
      if (err instanceof RecipientNotReadyError) {
        alert("Empfänger ist noch nicht vollständig eingerichtet. Bitte später erneut versuchen.");
        return;
      }
      const fallbackText = `↗️ Weitergeleitet:\n${rawText}`;
      ws.send({ type: "send_message", channelId: selectedChannel.id, ciphertext: fallbackText, contentType: "text" });
      onClose();
    } finally {
      setSending(false);
    }
  };

  const availableChannels = channels.filter(c => c.id !== currentChannelId);

  return (
    <div className={styles.forwardOverlay} onClick={onClose}>
      <div className={styles.forwardDialog} onClick={(e) => e.stopPropagation()}>
        <h4>Nachricht weiterleiten</h4>

        {/* Message preview */}
        <div className={styles.forwardPreview} style={isImage ? { whiteSpace: "normal", textAlign: "center" } : undefined}>
          {isImage ? (
            <AuthImage
              src={`/files/${fileInfo.fileId}`}
              alt={fileInfo.filename}
              className={styles.forwardPreviewImage}
            />
          ) : (
            humanizeFileTokens(rawText).slice(0, 100)
          )}
        </div>

        {/* Channel list */}
        <div className={styles.forwardList}>
          {availableChannels.map(c => {
            const name = channelDisplayName(c, currentUserId);
            const isSelected = selectedChannel?.id === c.id;
            return (
              <button
                key={c.id}
                className={`${styles.forwardItem} ${isSelected ? styles.forwardItemSelected : ""}`}
                onClick={() => setSelectedChannel(c)}
              >
                <Avatar name={name} size="xs" />
                <span>{name}</span>
                {isSelected && <span style={{ marginLeft: "auto" }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
          <button
            className={styles.forwardClose}
            onClick={onClose}
            style={{ flex: 1 }}
          >
            Abbrechen
          </button>
          <button
            className={styles.forwardClose}
            onClick={() => void handleForward()}
            disabled={!selectedChannel || sending}
            style={{
              flex: 1,
              background: selectedChannel ? "var(--color-accent, #7c3aed)" : undefined,
              color: selectedChannel ? "#fff" : undefined,
              opacity: selectedChannel ? 1 : 0.5,
              cursor: selectedChannel ? "pointer" : "not-allowed",
            }}
          >
            {sending ? "Wird gesendet…" : "Senden"}
          </button>
        </div>
      </div>
    </div>
  );
}
